/**
 * Future Closure API Service
 * Handles all API calls for the Future Forced Closure module
 */

import axios from '@/lib/http';
import api from '../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

let tokenGetter: (() => Promise<string | null>) | null = null;

export const setTokenGetter = (getter: () => Promise<string | null>) => {
  tokenGetter = getter;
};

const getAuthHeaders = async () => {
  if (tokenGetter) {
    const token = await tokenGetter();
    if (token) {
      return { Authorization: `Bearer ${token}` };
    }
  }
  return {};
};

// ============================================================================
// Configuration API
// ============================================================================

export const getFutureClosureConfig = async () => {
  const headers = await getAuthHeaders();
  const response = await axios.get(
    `${BACKEND_URL}/api/v1/future-closure/config`,
    { headers }
  );
  return response.data;
};

export const updateFutureClosureConfig = async (config: any) => {
  const headers = await getAuthHeaders();
  const response = await axios.put(
    `${BACKEND_URL}/api/v1/future-closure/config`,
    config,
    { headers }
  );
  return response.data;
};

// ============================================================================
// Calculation API
// ============================================================================

export interface FutureClosureCalculationParams {
  aggression?: number;
  use_best_base?: boolean;
  base_months?: number;
  custom_continuations?: {
    officer_salary?: number;
    employee_wages?: number;
    rent?: number;
    utilities?: number;
    insurance?: number;
    other_expenses?: number;
  };
  custom_one_time_costs?: {
    shutdown?: number;
    restart?: number;
  };
  closure_start_month?: string;
  closure_duration_months?: number;
  edge_case_config?: EdgeCaseConfig;
}

export const calculateFutureClosure = async (
  reportId: string,
  parameters: FutureClosureCalculationParams
) => {
  const headers = await getAuthHeaders();
  const response = await axios.post(
    `${BACKEND_URL}/api/v1/future-closure/calculate/${reportId}`,
    parameters,
    { headers }
  );
  return response.data;
};

// ============================================================================
// Phased Pro-Forma Assembler API (deterministic, Decimal, method-locked)
// ============================================================================
//
// The phased assembler is the accurate replacement for the legacy float
// engine. It computes each forced-closure damage component (spike offset,
// closure lost profits, recovery-period lost profits, property replacement,
// marketing/ICW, diminished business value) as a PV-discounted Decimal.
// The recovery period is DATA-DERIVED by default (RecoveryPeriodDeriver);
// an attorney may override, but an override is FLAGGED as not-data-supported.
// This preview endpoint is NON-PERSISTING.

export interface PhasedPreviewParams {
  closure_start_month: string;          // 'YYYY-MM' (required)
  closure_duration_months: number;      // required, positive
  spike_months?: string[];              // 'YYYY-MM' list
  discount_rate?: number;               // annual, default 0.05
  // Additive cost components (evidence-gated):
  property_replacement_cost?: number;
  replacement_date?: string;            // 'YYYY-MM-DD'
  marketing_icw_cost?: number;
  marketing_date?: string;              // 'YYYY-MM-DD'
  // Diminished Business Value (permanent closure only):
  permanent_closure?: boolean;
  value_before?: number;
  value_at_closure?: number;
  closure_valuation_date?: string;      // 'YYYY-MM-DD'
  // Attorney override of the data-derived recovery period (optional):
  epi_duration_days?: number;           // 30-720, divisible by 30
  ramp_up_curve?: 'linear' | 's_curve' | 'exponential';
}

export interface PhasedPreviewResult {
  success: boolean;
  report_id: string;
  data_source: string;
  spike_months: string[];
  closure_months: string[];
  ramp_months: string[];
  components: Array<{
    phase_label: string;
    month_or_span: string;
    gross: string;
    pv: string;
  }>;
  spike_offset: string;
  closure_lost_profits: string;
  ramp_up_loss: string;
  saved_costs_total: string;
  property_replacement: string;
  marketing_icw: string;
  diminished_business_value: string;
  total_undiscounted: string;
  total_pv: string;
  as_of: string;
  discount_rate: string;
  permanent_closure: boolean;
  // Recovery period derivation:
  recovery_period_derived: number | null;
  recovery_period_used: number | null;
  recovery_period_source: 'data_derived' | 'attorney_override' | 'projection_unsupported';
  data_supported: boolean;
  goodness_of_fit: number | null;
}

export const getPhasedPreview = async (
  reportId: string,
  parameters: PhasedPreviewParams
): Promise<PhasedPreviewResult> => {
  const headers = await getAuthHeaders();
  const response = await axios.post(
    `${BACKEND_URL}/api/v1/future-closure/calculate/${reportId}/phased-preview`,
    parameters,
    { headers }
  );
  return response.data;
};

// The PERSISTED phased result (engine_id='fc_phased'), authoritative + court-facing,
// written by compute_and_persist_fc_phased during analysis. Used by the accountant
// dashboard. Returns the same shape as the preview. Throws 404 if not computed yet.
export const getPhasedOutput = async (
  reportId: string
): Promise<PhasedPreviewResult> => {
  const headers = await getAuthHeaders();
  const response = await axios.get(
    `${BACKEND_URL}/api/v1/future-closure/phased-output/${reportId}`,
    { headers }
  );
  return response.data;
};

// ============================================================================
// Edge Case Analysis API
// ============================================================================

export interface EdgeCaseConfig {
  exclude_spike?: boolean;
  spike_year?: number | null;
  excluded_quarters?: string[];  // NEW: Quarter-specific exclusion ['Q1', 'Q2', 'Q3', 'Q4']
  accounting_basis?: 'accrual' | 'cash';
  lag_days?: number;
  payroll_mode?: 'fixed' | 'variable';
  icw_payroll?: boolean;
  entity_type?: 'restaurant' | 'retail' | 'factory' | 'nonprofit';
  shutdown_pct?: number | null;
  restart_pct?: number | null;
  multiplier_mode?: 'profits_only' | 'full_loss' | 'none';
}

export interface EdgeCaseFlags {
  event_anomaly?: {
    year: number;
    prior_year: number;
    spike_pct: number;
    concentration: string;
    reason: string;
    prior_revenue: number;
    current_revenue: number;
    excluded: boolean;
    // NEW: Quarter-specific exclusion fields
    excluded_quarters?: string[];
    quarter_adjustments?: Record<string, {
      original_revenue: number;
      expected_revenue: number;
      scale_factor: number;
      adjustment_pct: number;
      months: number[];
    }>;
    total_adjustment?: number;
    normalization_method?: 'full_year' | 'quarter_specific';
    granular_details?: {
      concentrated_quarters: string[];
      quarterly_growth: Record<string, number>;
      top_growth_months: Array<{
        month: number;
        month_name: string;
        growth_pct: number;
      }>;
      avg_growth_pct: number;
      concentration_desc: string;
    };
  } | null;
  accounting_basis?: {
    type: string;
    lag_days: number;
    normalized: boolean;
  };
  payroll_policy?: {
    mode: string;
    icw_applied: boolean;
    description: string;
  };
  expense_policy?: {
    entity: string;
    variable_pct: number;
    note: string | null;
  };
  missing_periods?: Array<{
    period: string;
    fill_method: string;
    naics: string;
    amount: number;
  }>;
  closure_costs?: {
    shutdown_pct: number;
    restart_pct: number;
    source: string;
  };
  multiplier?: {
    mode: string;
    value: number;
    applies_to: string;
  };
  trend_method?: {
    method: string;
    spike_excluded: boolean;
  };
}

export interface EdgeCaseAnalysisResult {
  report_id: string;
  flags: EdgeCaseFlags;
  dual_projections: {
    spiked_demand?: number;
    sustainable_demand?: number;
    difference?: number;
    difference_pct?: number;
  };
  yearly_revenue_totals: { [year: number]: number };
  config_applied: EdgeCaseConfig;
  data_points_processed: number;
  data_points_original: number;
}

export const analyzeEdgeCases = async (
  reportId: string,
  config: EdgeCaseConfig = {}
): Promise<EdgeCaseAnalysisResult> => {
  const headers = await getAuthHeaders();
  const response = await axios.post(
    `${BACKEND_URL}/api/v1/future-closure/analyze-edge-cases/${reportId}`,
    config,
    { headers }
  );
  return response.data;
};

// Edge Case Explanation types
export interface EdgeCaseExplanationResult {
  success: boolean;
  report_id: string;
  explanation_text: string;
  sections: {
    revenue_anomaly?: string;
    payroll_treatment?: string;
    data_gaps?: string;
    closure_costs?: string;
    monte_carlo?: string;
    overview?: string;
  };
  summary: string;
  word_count: number;
  generated_by: string;
}

export const generateEdgeCaseExplanation = async (
  reportId: string,
  params: { edge_case_config?: EdgeCaseConfig; monte_carlo_results?: any } = {}
): Promise<EdgeCaseExplanationResult> => {
  const headers = await getAuthHeaders();
  const response = await axios.post(
    `${BACKEND_URL}/api/v1/future-closure/generate-explanation/${reportId}`,
    params,
    { headers }
  );
  return response.data;
};

export interface MonteCarloParams {
  aggression?: number;
  iterations?: number;
  noise_pct?: number;
}

export interface SensitivityParams {
  aggression?: number;
  use_best_base?: boolean;
  base_months?: number;
  variation_pct?: number;
}

export const runSensitivityAnalysis = async (
  reportId: string,
  parameters: SensitivityParams
) => {
  const headers = await getAuthHeaders();
  const response = await axios.post(
    `${BACKEND_URL}/api/v1/future-closure/calculate/${reportId}/sensitivity`,
    parameters,
    { headers }
  );
  return response.data;
};

export const runMonteCarloSimulation = async (
  reportId: string,
  parameters: MonteCarloParams
) => {
  const headers = await getAuthHeaders();
  const response = await axios.post(
    `${BACKEND_URL}/api/v1/future-closure/calculate/${reportId}/monte-carlo`,
    parameters,
    { headers }
  );
  return response.data;
};

export const getFutureClosureCalculations = async (reportId: string) => {
  const headers = await getAuthHeaders();
  const response = await axios.get(
    `${BACKEND_URL}/api/v1/future-closure/calculations/${reportId}`,
    { headers }
  );
  return response.data;
};

export const getFutureClosureCalculation = async (calculationId: string) => {
  const headers = await getAuthHeaders();
  const response = await axios.get(
    `${BACKEND_URL}/api/v1/future-closure/calculation/${calculationId}`,
    { headers }
  );
  return response.data;
};

// ============================================================================
// Case-Specific Configuration API (P3)
// ============================================================================

export interface CaseConfig {
  aggression?: number;
  use_best_base?: boolean;
  base_months?: number;
  closure_duration_months?: number;
  custom_continuations?: {
    officer_salary?: number;
    employee_wages?: number;
    rent?: number;
    utilities?: number;
    insurance?: number;
    other_expenses?: number;
  };
  custom_one_time_costs?: {
    shutdown?: number;
    restart?: number;
  } | null;
  notes?: string;
}

export const getCaseConfig = async (reportId: string) => {
  const headers = await getAuthHeaders();
  const response = await axios.get(
    `${BACKEND_URL}/api/v1/future-closure/case-config/${reportId}`,
    { headers }
  );
  return response.data;
};

export const saveCaseConfig = async (reportId: string, config: CaseConfig) => {
  const headers = await getAuthHeaders();
  const response = await axios.put(
    `${BACKEND_URL}/api/v1/future-closure/case-config/${reportId}`,
    config,
    { headers }
  );
  return response.data;
};

export const deleteCaseConfig = async (reportId: string) => {
  const headers = await getAuthHeaders();
  const response = await axios.delete(
    `${BACKEND_URL}/api/v1/future-closure/case-config/${reportId}`,
    { headers }
  );
  return response.data;
};

// ============================================================================
// Data Validation API (P4)
// ============================================================================

export const validateReportData = async (reportId: string) => {
  const headers = await getAuthHeaders();
  const response = await axios.get(
    `${BACKEND_URL}/api/v1/future-closure/validate/${reportId}`,
    { headers }
  );
  return response.data;
};

// ============================================================================
// Scenario Save & Compare API
// ============================================================================

export interface SavedScenario {
  id: string;
  report_id: string;
  name: string;
  notes: string;
  calculation_id: string;
  parameters: {
    aggression?: number;
    use_best_base?: boolean;
    base_months?: number;
    closure_duration_months?: number;
  };
  results: {
    total_demand: number;
    lost_profits: number;
    shutdown_costs: number;
    restart_costs: number;
    pro_forma_summary?: any;
    one_time_costs?: any;
    continuation_percentages?: Record<string, number>;
  };
  created_by: string;
  created_at: string;
}

export const getSavedScenarios = async (reportId: string): Promise<{ scenarios: SavedScenario[]; count: number }> => {
  const headers = await getAuthHeaders();
  const response = await axios.get(
    `${BACKEND_URL}/api/v1/future-closure/scenarios/${reportId}`,
    { headers }
  );
  return response.data;
};

export const saveScenario = async (
  reportId: string,
  name: string,
  calculationId: string,
  notes?: string
): Promise<{ success: boolean; scenario: SavedScenario }> => {
  const headers = await getAuthHeaders();
  const response = await axios.post(
    `${BACKEND_URL}/api/v1/future-closure/scenarios/${reportId}`,
    { name, calculation_id: calculationId, notes: notes || '' },
    { headers }
  );
  return response.data;
};

export const deleteScenario = async (reportId: string, scenarioId: string) => {
  const headers = await getAuthHeaders();
  const response = await axios.delete(
    `${BACKEND_URL}/api/v1/future-closure/scenarios/${reportId}/${scenarioId}`,
    { headers }
  );
  return response.data;
};

export const compareScenarios = async (reportId: string, scenarioIds: string[]) => {
  const headers = await getAuthHeaders();
  const response = await axios.post(
    `${BACKEND_URL}/api/v1/future-closure/scenarios/${reportId}/compare`,
    { scenario_ids: scenarioIds },
    { headers }
  );
  return response.data;
};

// ============================================================================
// PDF Export API
// ============================================================================

export const downloadFutureClosurePDF = async (reportId: string, calculationId?: string) => {
  let url = `/api/v1/future-closure/export/${reportId}/pdf`;
  if (calculationId) {
    url += `?calculation_id=${calculationId}`;
  }
  
  // Use centralized API utility for blob download.
  // (`responseType: 'blob'` removed — axios-era option not in
  // ApiOptions. apiFetch auto-returns a Blob for pdf/octet-stream/vnd
  // content types. 2026-04-26 archaeology pass.)
  const blob = await api.get(url, {
    showErrorToast: true,
    headers: { 'Accept': 'application/pdf' }
  });
  
  const downloadUrl = window.URL.createObjectURL(blob);
  
  // Create filename
  const filename = `Future_Closure_Report_${reportId}.pdf`;
  
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
  
  return { success: true, filename };
};

export default {
  setTokenGetter,
  getFutureClosureConfig,
  updateFutureClosureConfig,
  calculateFutureClosure,
  getPhasedPreview,
  runSensitivityAnalysis,
  runMonteCarloSimulation,
  getFutureClosureCalculations,
  getFutureClosureCalculation,
  downloadFutureClosurePDF,
  getCaseConfig,
  saveCaseConfig,
  deleteCaseConfig,
  validateReportData,
  getSavedScenarios,
  saveScenario,
  deleteScenario,
  compareScenarios,
};
